import { useEffect, useRef, useState } from "react";
import {
    Download, Share2, Link2, Eye, Briefcase, Rocket, Store, Handshake,
    CircleDollarSign, Compass, FlaskConical, HeartHandshake,
} from "lucide-react";
import { toast } from "sonner";
import { SectionHead, Reveal } from "@/components/Shared";

const TARGET = new Date("2027-01-29T09:00:00+05:30").getTime();
const SITE = "https://www.startupmahabazaar.com";
const daysLeft = () => Math.max(0, Math.ceil((TARGET - Date.now()) / 86400000));

const TRIBES = [
    { id: "Visitor", icon: Eye, tint: "#E8CF8F", tagline: "The Curious Soul of the Bazaar" },
    { id: "Delegate", icon: Briefcase, tint: "#C9A24B", tagline: "The Statesperson of Ideas" },
    { id: "Startup", icon: Rocket, tint: "#D9A441", tagline: "The Dreamer Who Builds" },
    { id: "Exhibitor", icon: Store, tint: "#C96A4B", tagline: "The Maker on the Grand Floor" },
    { id: "Partner", icon: Handshake, tint: "#F0DFAF", tagline: "The Architect of the Bazaar" },
    { id: "Investor", icon: CircleDollarSign, tint: "#D4B06A", tagline: "The Fuel Behind the Fire" },
    { id: "Mentor", icon: Compass, tint: "#A8B85A", tagline: "The Guide of Journeys" },
    { id: "Incubator / CoE", icon: FlaskConical, tint: "#B8A24B", tagline: "The Cradle of Champions" },
    { id: "Volunteer", icon: HeartHandshake, tint: "#C94B6A", tagline: "The Heartbeat of the Bazaar" },
];

const loadImg = (src) =>
    new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = src;
    });

async function drawSaathi(canvas, tribe, name) {
    const W = 1080, H = 1920;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    await Promise.all([
        document.fonts.load('600 90px "Fraunces"'),
        document.fonts.load('italic 400 36px "Fraunces"'),
        document.fonts.load('500 26px "Jost"'),
        document.fonts.load('400 24px "Jost"'),
    ]);
    const emblem = await loadImg("/emblem.png");
    const cx = W / 2;
    const canSpace = "letterSpacing" in ctx;
    const setSpacing = (px) => {
        if (canSpace) ctx.letterSpacing = `${px}px`;
    };

    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#1C060C");
    bg.addColorStop(0.55, "#140409");
    bg.addColorStop(1, "#26070F");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(201,162,75,0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 44) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 44) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    const glow = ctx.createRadialGradient(cx, H * 0.34, 10, cx, H * 0.34, W * 0.44);
    glow.addColorStop(0, "rgba(165,28,48,0.42)");
    glow.addColorStop(1, "rgba(165,28,48,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    const arch = (w, color, lw) => {
        ctx.beginPath();
        ctx.moveTo(cx - w / 2, 1600);
        ctx.lineTo(cx - w / 2, 90 + w / 2);
        ctx.arc(cx, 90 + w / 2, w / 2, Math.PI, 0);
        ctx.lineTo(cx + w / 2, 1600);
        ctx.strokeStyle = color;
        ctx.lineWidth = lw;
        ctx.stroke();
    };
    arch(820, "rgba(201,162,75,0.5)", 2);
    arch(786, "rgba(201,162,75,0.22)", 1);
    ctx.strokeStyle = "rgba(201,162,75,0.4)";
    ctx.setLineDash([5, 7]);
    ctx.beginPath(); ctx.moveTo(cx - 470, 1600); ctx.lineTo(cx + 470, 1600); ctx.stroke();
    ctx.setLineDash([]);

    const ew = (emblem.width / emblem.height) * 175;
    ctx.drawImage(emblem, cx - ew / 2, 150, ew, 175);

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const goldGrad = ctx.createLinearGradient(cx - 300, 0, cx + 300, 0);
    goldGrad.addColorStop(0, "#8C6A2F");
    goldGrad.addColorStop(0.28, "#E8CF8F");
    goldGrad.addColorStop(0.52, "#C9A24B");
    goldGrad.addColorStop(0.76, "#F2E2B0");
    goldGrad.addColorStop(1, "#8C6A2F");

    const divider = (y) => {
        ctx.strokeStyle = "rgba(201,162,75,0.45)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(cx - 190, y); ctx.lineTo(cx - 16, y); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx + 16, y); ctx.lineTo(cx + 190, y); ctx.stroke();
        ctx.save();
        ctx.translate(cx, y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = "#C9A24B";
        ctx.fillRect(-4, -4, 8, 8);
        ctx.restore();
    };

    setSpacing(12);
    ctx.font = '500 26px "Jost"';
    ctx.fillStyle = "#C9A24B";
    ctx.fillText("STARTUP MAHABAZAAR 2027", cx, 435);

    divider(478);

    setSpacing(14);
    ctx.font = '500 24px "Jost"';
    ctx.fillStyle = "rgba(179,164,147,0.9)";
    ctx.fillText("OFFICIAL", cx, 566);

    setSpacing(4);
    ctx.font = '600 70px "Fraunces"';
    ctx.fillStyle = goldGrad;
    ctx.fillText("MAHABAZAAR", cx, 648);

    setSpacing(10);
    ctx.font = '600 118px "Fraunces"';
    ctx.fillText("SAATHI", cx, 786);

    // tribe pill
    setSpacing(8);
    ctx.font = '500 30px "Jost"';
    const label = tribe.id.toUpperCase();
    const tw = ctx.measureText(label).width;
    ctx.strokeStyle = tribe.tint;
    ctx.lineWidth = 1.6;
    ctx.strokeRect(cx - tw / 2 - 46, 838, tw + 92, 62);
    ctx.fillStyle = tribe.tint;
    ctx.fillText(label, cx, 881);

    setSpacing(0);
    ctx.font = 'italic 400 36px "Fraunces"';
    ctx.fillStyle = "rgba(232,207,143,0.9)";
    ctx.fillText(tribe.tagline, cx, 962);

    // name — auto-fit
    const displayName = name.trim() || "YOUR NAME";
    let size = 96;
    ctx.font = `600 ${size}px "Fraunces"`;
    while (ctx.measureText(displayName).width > 880 && size > 40) {
        size -= 4;
        ctx.font = `600 ${size}px "Fraunces"`;
    }
    ctx.fillStyle = name.trim() ? goldGrad : "rgba(232,207,143,0.4)";
    ctx.fillText(displayName, cx, 1130);

    divider(1194);

    setSpacing(8);
    ctx.font = '500 30px "Jost"';
    ctx.fillStyle = "#C9A24B";
    ctx.fillText(`${daysLeft()} DAYS TO GO`, cx, 1282);

    setSpacing(5);
    ctx.font = '400 24px "Jost"';
    ctx.fillStyle = "rgba(243,234,216,0.85)";
    ctx.fillText("29–31 JANUARY 2027 · MSIT & MSI, NEW DELHI", cx, 1358);

    setSpacing(0);
    ctx.font = 'italic 400 26px "Fraunces"';
    ctx.fillStyle = "rgba(232,207,143,0.65)";
    ctx.fillText("Bazaar of Ideas · Innovation · Impact", cx, 1480);

    setSpacing(6);
    ctx.font = '500 20px "Jost"';
    ctx.fillStyle = "#C9A24B";
    ctx.fillText("startupmahabazaar.com", cx, 1540);
    setSpacing(0);

    ctx.strokeStyle = "rgba(201,162,75,0.35)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(14, 14, W - 28, H - 28);
}

export default function SaathiCards() {
    const [tribe, setTribe] = useState(TRIBES[2]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const canvasRef = useRef(null);

    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            drawSaathi(canvasRef.current, tribe, name).catch(() => alive && toast.error("Could not render the card right now."));
        }, 180);
        return () => {
            alive = false;
            clearTimeout(t);
        };
    }, [tribe, name]);

    const caption = `I'm officially a MahaBazaar Saathi (${tribe.id}) — ${daysLeft()} days to Startup MahaBazaar 2027, India's Grand Festival of Startups, Innovation & Entrepreneurship. 29–31 January 2027 · MSIT & MSI, New Delhi. Join me: ${SITE}`;

    const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

    const download = async () => {
        const blob = await toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mahabazaar-saathi-${tribe.id.toLowerCase().replace(/[\s/]+/g, "-")}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Your Saathi card is saved — wear it proudly.");
    };

    const shareNative = async () => {
        setBusy(true);
        try {
            const blob = await toBlob();
            const file = new File([blob], "mahabazaar-saathi.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: "MahaBazaar Saathi", text: caption });
            } else if (navigator.share) {
                await navigator.share({ title: "MahaBazaar Saathi", text: caption });
            } else {
                await download();
            }
        } catch {
            /* user dismissed the share sheet */
        } finally {
            setBusy(false);
        }
    };

    const copyCaption = async () => {
        try {
            await navigator.clipboard.writeText(caption);
            toast.success("Caption copied — paste it with your card.");
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = caption;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                ta.remove();
                toast.success("Caption copied — paste it with your card.");
            } catch {
                toast.error("Could not copy right now.");
            }
        }
    };

    return (
        <section id="saathi" className="py-24 md:py-36 relative overflow-hidden noise scroll-mt-24" data-testid="saathi-section">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-[30rem] h-[30rem] rounded-full opacity-20"
                    style={{ background: "radial-gradient(circle, rgba(92,16,28,0.9), transparent 65%)" }} />
            </div>
            <div className="container-x relative">
                <SectionHead
                    eyebrow="17 · MahaBazaar Saathi"
                    title={<>Carry the Bazaar <span className="gold-text">in Your Story</span></>}
                    sub="Every participant is a Saathi — a companion of the bazaar. Choose your tribe, print your name, and share your official story card with the world."
                />

                <div className="mt-16 grid lg:grid-cols-2 gap-14 items-start">
                    {/* preview — first on mobile for instant delight */}
                    <Reveal className="order-first lg:order-last">
                        <div className="lg:sticky lg:top-32">
                            <div className="relative mx-auto w-full max-w-[250px] sm:max-w-[280px] aspect-[9/16] border border-[var(--line)] bg-[#1C060C] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.85)] floaty">
                                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-testid="saathi-canvas" />
                            </div>
                            <p className="mt-5 text-center text-[0.6rem] tracking-[0.3em] uppercase text-muted-luxe">
                                Story Format · 1080 × 1920 · Live Preview
                            </p>
                            <div className="mt-6 grid grid-cols-1 gap-2.5 max-w-[280px] mx-auto">
                                <button onClick={shareNative} disabled={busy} data-testid="saathi-share-btn" className="btn btn-gold !py-3.5 w-full">
                                    <Share2 size={13} /> {busy ? "Preparing…" : "Share Your Card"}
                                </button>
                                <div className="grid grid-cols-2 gap-2.5">
                                    <button onClick={download} data-testid="saathi-download-btn" className="btn btn-outline !py-3 !px-3">
                                        <Download size={13} /> Save PNG
                                    </button>
                                    <button onClick={copyCaption} data-testid="saathi-copy-btn" className="btn btn-dark !py-3 !px-3">
                                        <Link2 size={13} /> Caption
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* controls */}
                    <div>
                        <Reveal>
                            <span className="eyebrow no-lines">Step 01 — Choose Your Tribe</span>
                            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Saathi category">
                                {TRIBES.map((t) => {
                                    const active = tribe.id === t.id;
                                    return (
                                        <button key={t.id} onClick={() => setTribe(t)} role="radio" aria-checked={active}
                                            data-testid={`saathi-tribe-${t.id.toLowerCase().replace(/[\s/]+/g, "-")}`}
                                            className={`border p-4 text-left transition-all duration-400 group ${
                                                active
                                                    ? "border-[var(--gold)] bg-[rgba(201,162,75,0.07)] shadow-[0_0_30px_rgba(201,162,75,0.12)]"
                                                    : "hairline hover:border-[var(--line)]"
                                            }`}>
                                            <t.icon size={19} strokeWidth={1.3} style={{ color: t.tint }}
                                                className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:scale-110" />
                                            <span className="block mt-3 text-[0.62rem] tracking-[0.2em] uppercase text-[var(--ivory)]">{t.id}</span>
                                            <span className="block mt-1.5 font-display italic text-[0.72rem] leading-snug" style={{ color: active ? t.tint : "var(--muted)" }}>
                                                {t.tagline}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Reveal>

                        <Reveal delay={120}>
                            <span className="eyebrow no-lines mt-12 inline-flex">Step 02 — Print Your Name</span>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={22}
                                placeholder="Your name or startup name"
                                data-testid="saathi-name-input"
                                className="field mt-5 !py-4 text-lg font-display"
                            />
                            <p className="mt-3 text-[0.6rem] tracking-[0.24em] uppercase text-muted-luxe/70">
                                Appears in gold on your card — up to 22 characters
                            </p>
                        </Reveal>

                        <Reveal delay={200}>
                            <div className="mt-10 border hairline p-5 bg-[rgba(201,162,75,0.03)]">
                                <p className="text-[0.55rem] tracking-[0.3em] uppercase text-[var(--gold)] mb-2">Your Share Caption</p>
                                <p className="text-xs text-[var(--ivory)]/70 leading-relaxed" data-testid="saathi-caption-preview">{caption}</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
